import {
  Button,
  MenuButton,
  Menu,
  MenuList,
  MenuItem,
  Flex,
  Text,
  MenuDivider,
  useToast,
  Spinner,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import axios from 'axios';
import FileSaver from 'file-saver';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { utils as commonUtils } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  InvoiceFragmentGQLType,
  InvoiceReportGQLType,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  GetInvoiceByIdDocumentGQLType,
  GetInvoicesByLoanIdDocumentGQLType,
  GetInvoicesByOrganizationIdDocumentGQLType,
  useMarkInvoiceAsPaidMutation,
  useMarkInvoiceAsCanceledMutation,
  useUpdateInvoiceDueDateMutation,
  GetLatestInvoiceFacilityFeeAccrualEndDateDocumentGQLType,
  GetPaymentInKindServicingFeeDetailsForCreditFacilitiesDocumentGQLType,
  GetPrincipalPaymentMetricsByLoanIdDocumentGQLType,
  useGetLoanMetricsForInvoiceIdQuery,
  GetInvoiceConsolidatedDisbursementsByInvoiceIdDocumentGQLType,
} from '../../requests/invoices.gql.generated';
import { EditInvoiceDueDateModal } from './EditInvoiceDueDateModal';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { CancelAndRedoInvoiceButton } from 'packages/invoices/ui/invoiceDetail/CancelAndRedoInvoiceButton';
import { MarkInvoiceAsPaidModal } from 'packages/invoices/ui/invoiceDetail/MarkInvoiceAsPaidModal';
import {
  GetCurrentLiabilityAccountsAndAccrualsForLoanDocumentGQLType,
  GetResolvedLoanByIdDocumentGQLType,
} from 'packages/loans/requests/loans.gql.generated';
import { useDownloadReportUrl } from 'packages/reporting/hooks/useDownloadReportUrl';
import { useGetReportS3DownloadUrlByReportingWorkleaseRecordIdLazyQueryGQLType } from 'packages/reporting/requests/reporting.gql.generated';
import {
  GetDailyAccountActivityDocumentGQLType,
  GetDailyResolvedAggregateLineItemEntriesDocumentGQLType,
  GetResolvedDetailedEntriesForFacilitiesDocumentGQLType,
} from 'packages/transactions/requests/transactions.gql.generated';
import { getErrorMessageFromApolloError } from '~/contexts/ApolloClientProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

const RouteParamsStruct = z.object({
  workleaseId: z.string().optional(),
});

const DownloadLiveInvoiceButton = ({
  invoiceReport,
}: {
  invoiceReport: InvoiceReportGQLType;
}) => {
  const { getReportDownloadUrl, isLoading } = useDownloadReportUrl(
    invoiceReport,
  );

  return (
    <MenuItem
      icon={isLoading ? <Spinner /> : <Icon icon="arrow-down-to-bracket" />}
      color={colors.gray[800]}
      onClick={getReportDownloadUrl}
      isDisabled={isLoading}
    >
      Download Invoice
    </MenuItem>
  );
};

export const InvoiceDetailActionsMenu = ({
  invoice,
  reverseInvoicePayment,
  isReverseInvoicePaymentLoading,
}: {
  invoice: InvoiceFragmentGQLType;
  reverseInvoicePayment?: () => void;
  isReverseInvoicePaymentLoading: boolean;
}) => {
  const toast = useToast();
  const params = useParams();
  const { workleaseId } = RouteParamsStruct.parse(params);
  const { user } = useUserAuthOrThrow();

  const {
    isOpen: isEditDueDateModalOpen,
    onOpen: onEditDueDateModalOpen,
    onClose: onEditDueDateModalClose,
  } = useDisclosure();

  const {
    isOpen: isMarkInvoiceAsPaidModalOpen,
    onOpen: onMarkInvoiceAsPaidModalOpen,
    onClose: onMarkInvoiceAsPaidModalClose,
  } = useDisclosure();

  const [
    currentPollingWorkleaseRecordId,
    setCurrentPollingWorkleaseRecordId,
  ] = useState<string>();
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);

  const getLoanMetricsQuery = useGetLoanMetricsForInvoiceIdQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      invoiceId: invoice.id,
    },
  });
  if (getLoanMetricsQuery.error) throw getLoanMetricsQuery.error;

  const [
    getReportS3DownloadUrlByWorklease,
    { data: reportS3DownloadUrlByWorkleaseData },
  ] = useGetReportS3DownloadUrlByReportingWorkleaseRecordIdLazyQueryGQLType({
    pollInterval: currentPollingWorkleaseRecordId ? 1000 : 0, // poll every second if 'currentPollingReport' exists
    fetchPolicy: 'no-cache',
  });

  const [
    markInvoiceAsPaid,
    { loading: isMarkInvoiceAsPaidLoading },
  ] = useMarkInvoiceAsPaidMutation({
    refetchQueries: [
      GetInvoiceByIdDocumentGQLType,
      GetInvoicesByOrganizationIdDocumentGQLType,
      GetInvoicesByLoanIdDocumentGQLType,
      GetResolvedLoanByIdDocumentGQLType,
      GetCurrentLiabilityAccountsAndAccrualsForLoanDocumentGQLType,
      GetPaymentInKindServicingFeeDetailsForCreditFacilitiesDocumentGQLType,
      GetPrincipalPaymentMetricsByLoanIdDocumentGQLType,
      // refetch loan transactions when paying down invoice
      GetDailyAccountActivityDocumentGQLType,
      GetDailyResolvedAggregateLineItemEntriesDocumentGQLType,
      GetResolvedDetailedEntriesForFacilitiesDocumentGQLType,
    ],
    onCompleted: () => {
      toast({
        ...successToastConfig,
        title: 'Successfully marked invoice as paid',
      });
    },
  });

  const [
    markInvoiceAsCanceled,
    {
      loading: isMarkInvoiceAsCanceledLoading,
      error: markInvoiceAsCanceledError,
    },
  ] = useMarkInvoiceAsCanceledMutation({
    refetchQueries: [
      GetInvoiceByIdDocumentGQLType,
      GetInvoicesByOrganizationIdDocumentGQLType,
      GetInvoicesByLoanIdDocumentGQLType,
      GetInvoiceConsolidatedDisbursementsByInvoiceIdDocumentGQLType,
      GetResolvedLoanByIdDocumentGQLType,
      GetLatestInvoiceFacilityFeeAccrualEndDateDocumentGQLType,
      GetCurrentLiabilityAccountsAndAccrualsForLoanDocumentGQLType,
      GetPrincipalPaymentMetricsByLoanIdDocumentGQLType,
    ],
    onCompleted: () => {
      toast({
        ...successToastConfig,
        title: 'Successfully canceled invoice',
      });
    },
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isCustomerFacing()
        ? 'Unable to cancel invoice. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  const [
    updateInvoiceDueDate,
    {
      loading: isUpdateInvoiceDueDateLoading,
      error: updateInvoiceDueDateError,
    },
  ] = useUpdateInvoiceDueDateMutation({
    refetchQueries: [
      GetInvoiceByIdDocumentGQLType,
      GetInvoicesByOrganizationIdDocumentGQLType,
      GetInvoicesByLoanIdDocumentGQLType,
      GetResolvedLoanByIdDocumentGQLType,
    ],
    onCompleted: () => {
      toast({
        ...successToastConfig,
        title: 'Successfully updated invoice due date',
      });
    },
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isCustomerFacing()
        ? 'Unable to update invoice due date. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  const saveFile = useCallback(
    async (s3DownloadUrl: string) => {
      const response = await axios({
        url: s3DownloadUrl,
        method: 'GET',
        responseType: 'blob',
      });

      const fileBlob = new Blob([response.data], {
        type: response.data.type,
      });
      const fileName = `invoice-${invoice.id}`;
      FileSaver.saveAs(fileBlob, fileName);
    },
    [invoice.id],
  );

  const onClickDownloadInvoiceFromWorklease = useCallback(async () => {
    // it's possible that there are no invoice reports for an invoice
    // or that the invoice report has not finished generating yet
    // in this case, we will poll for the invoice report via the worklease record id
    if (workleaseId) {
      setCurrentPollingWorkleaseRecordId(workleaseId);

      getReportS3DownloadUrlByWorklease({
        variables: {
          reportConfigType: 'invoice',
          reportingWorkleaseRecordId: workleaseId,
        },
      });

      setIsDownloadLoading(true);
      toast({ ...successToastConfig, title: 'Generating invoice download' });
    }
  }, [workleaseId, getReportS3DownloadUrlByWorklease, toast]);

  useEffect(() => {
    const handleWorkleaseStatus = async () => {
      // if worklease is initialized state, return and continue polling
      if (
        !reportS3DownloadUrlByWorkleaseData?.getReportS3DownloadUrlByReportingWorkleaseRecordId ||
        reportS3DownloadUrlByWorkleaseData
          .getReportS3DownloadUrlByReportingWorkleaseRecordId
          .workleaseStatus === 'initialized'
      ) {
        return;
      }

      const {
        getReportS3DownloadUrlByReportingWorkleaseRecordId: {
          workleaseStatus,
          s3DownloadUrl,
          error,
        },
      } = reportS3DownloadUrlByWorkleaseData;

      // if worklease is in error state, toast error and stop polling
      if (workleaseStatus === 'error' || !s3DownloadUrl) {
        const errorToastDescription =
          commonUtils.EnvUtils.isCustomerFacing() || !error ? undefined : error;

        toast({
          ...errorToastConfig,
          title: `Failed to retrieve valid download URL for invoice`,
          description: errorToastDescription,
        });

        setIsDownloadLoading(false);
        setCurrentPollingWorkleaseRecordId(undefined);
        return;
      }

      // if worklease is in success state, download file from S3 url and stop polling
      if (workleaseStatus === 'success') {
        setCurrentPollingWorkleaseRecordId(undefined);

        try {
          await saveFile(s3DownloadUrl);
        } catch (err) {
          errorToastConfig.title = commonUtils.EnvUtils.isCustomerFacing()
            ? 'Failed to retrieve invoice download. Please contact Finley for support.'
            : JSON.stringify(err);

          toast(errorToastConfig);
        } finally {
          setIsDownloadLoading(false);
        }
      }
    };

    if (currentPollingWorkleaseRecordId) {
      // if there is a worklease we are currently polling for,
      // handle the worklease results
      handleWorkleaseStatus();
    }
  }, [
    reportS3DownloadUrlByWorkleaseData,
    toast,
    currentPollingWorkleaseRecordId,
    invoice.id,
    saveFile,
  ]);

  if (markInvoiceAsCanceledError) {
    throw markInvoiceAsCanceledError;
  }

  if (updateInvoiceDueDateError) {
    throw updateInvoiceDueDateError;
  }

  const canUpdateInvoice = Boolean(
    invoice.status !== 'paid' &&
      invoice.status !== 'canceled' &&
      invoice.paymentDestinationOrganizationId === user.organizationId,
  );

  // We want to prevent users from undo-ing payments that include principal
  // and have occurred prior to any fund transfer activity since undo-ing
  // this payment could muddy up the balance reversals between LTD and
  // Admin Agent Principal Payable since they have already changed prior to
  // this payment date from the fund transfer activity

  // disabling this for now as requested by product
  // const isPrincipalPaymentAfterLatestFundTransferDate =
  //   // Only care about fund transfer activity for principal payments
  //   !getLoanMetricsQuery.data?.result.hasPrincipalPayment ||
  //   // If dates aren't set, then doesn't matter
  //   !getLoanMetricsQuery.data?.result.latestFundTransferDate ||
  //   !invoice.paymentDate ||
  //   // Actually checking payment is after latest fund transfer date
  //   invoice.paymentDate >=
  //     getLoanMetricsQuery.data.result.latestFundTransferDate;

  // only allow undo if the invoice is paid and the user is the payment destination organization
  const canUndoInvoicePayment = Boolean(
    invoice.status === 'paid' &&
      invoice.paymentDestinationOrganizationId === user.organizationId &&
      // isPrincipalPaymentAfterLatestFundTransferDate &&
      reverseInvoicePayment,
  );

  const invoiceReport = invoice.invoiceReports?.find(
    (report) => report.reportType === 'invoice',
  );

  return (
    <Menu>
      <MenuButton
        as={Button}
        variant="secondary"
        colorScheme="v2-aurora"
        isDisabled={
          isReverseInvoicePaymentLoading ||
          isMarkInvoiceAsPaidLoading ||
          isMarkInvoiceAsCanceledLoading
        }
      >
        <Flex alignItems="center">
          <Text mr="8px">Actions</Text>
          <Icon icon="chevron-down" />
        </Flex>
      </MenuButton>
      <MenuList
        backgroundColor="white"
        borderRadius="8px"
        borderWidth="1px"
        borderColor={colors.gray[100]}
        boxShadow="0px 1px 2px 0px rgba(0, 0, 0, 0.30), 0px 2px 6px 2px rgba(0, 0, 0, 0.15)"
      >
        {canUpdateInvoice && (
          <MenuItem
            icon={
              isMarkInvoiceAsPaidLoading ? <Spinner /> : <Icon icon="check" />
            }
            color={colors.gray[800]}
            onClick={onMarkInvoiceAsPaidModalOpen}
            isDisabled={isMarkInvoiceAsPaidLoading}
          >
            Mark Paid
          </MenuItem>
        )}
        {canUpdateInvoice && isMarkInvoiceAsPaidModalOpen && (
          <MarkInvoiceAsPaidModal
            markInvoiceAsPaid={async (paymentDate: Util.FullDateStr) => {
              try {
                await markInvoiceAsPaid({
                  variables: {
                    invoiceId: invoice.id,
                    invoicePaymentDate: paymentDate,
                  },
                });

                onMarkInvoiceAsPaidModalClose();
              } catch (error) {
                toast({
                  ...errorToastConfig,
                  duration: 8000,
                  title: getErrorMessageFromApolloError({
                    error,
                    defaultMessage:
                      'Unable to mark invoice as paid. Please contact Finley for support.',
                  }),
                });
              }
            }}
            isMarkInvoiceAsPaidLoading={isMarkInvoiceAsPaidLoading}
            isOpen={isMarkInvoiceAsPaidModalOpen}
            onClose={onMarkInvoiceAsPaidModalClose}
            invoice={invoice}
          />
        )}
        {canUpdateInvoice && <MenuDivider />}

        {canUpdateInvoice && (
          <MenuItem
            icon={
              isMarkInvoiceAsCanceledLoading ? <Spinner /> : <Icon icon="x" />
            }
            color={colors.gray[800]}
            onClick={() =>
              markInvoiceAsCanceled({ variables: { invoiceId: invoice.id } })
            }
            isDisabled={isMarkInvoiceAsCanceledLoading}
          >
            Cancel Invoice
          </MenuItem>
        )}
        {canUpdateInvoice && <MenuDivider />}

        {canUpdateInvoice && (
          <>
            <CancelAndRedoInvoiceButton invoiceId={invoice.id} />
            <MenuDivider />
          </>
        )}

        {canUpdateInvoice && (
          <MenuItem
            icon={
              isUpdateInvoiceDueDateLoading ? <Spinner /> : <Icon icon="edit" />
            }
            color={colors.gray[800]}
            onClick={onEditDueDateModalOpen}
            isDisabled={isEditDueDateModalOpen}
          >
            Edit Due Date
          </MenuItem>
        )}
        {canUpdateInvoice && isEditDueDateModalOpen && (
          <EditInvoiceDueDateModal
            invoiceExistingDueDate={invoice.dueDate}
            updateInvoiceDueDate={async (newDueDate: Util.FullDateStr) => {
              await updateInvoiceDueDate({
                variables: {
                  invoiceId: invoice.id,
                  newDueDate,
                },
              });
            }}
            isUpdateInvoiceDueDateLoading={isUpdateInvoiceDueDateLoading}
            isOpen={isEditDueDateModalOpen}
            onClose={onEditDueDateModalClose}
          />
        )}
        {canUpdateInvoice && <MenuDivider />}

        {/* <Tooltip
          isDisabled={isPrincipalPaymentAfterLatestFundTransferDate}
          label="Cannot undo payment that occurred prior to latest fund transfer date."
          placement="left"
        > */}
        <MenuItem
          icon={
            isReverseInvoicePaymentLoading ? <Spinner /> : <Icon icon="undo" />
          }
          color={colors.gray[800]}
          onClick={reverseInvoicePayment}
          isDisabled={
            getLoanMetricsQuery.loading ||
            isReverseInvoicePaymentLoading ||
            !canUndoInvoicePayment
          }
        >
          Undo Invoice Payment
        </MenuItem>
        {/* </Tooltip> */}

        <MenuDivider />

        {/* Download Invoice Menu Item Variations */}
        {!!invoiceReport && (
          <DownloadLiveInvoiceButton invoiceReport={invoiceReport} />
        )}
        {!invoiceReport && !!workleaseId && (
          <MenuItem
            icon={
              isDownloadLoading ? (
                <Spinner />
              ) : (
                <Icon icon="arrow-down-to-bracket" />
              )
            }
            color={colors.gray[800]}
            onClick={onClickDownloadInvoiceFromWorklease}
            isDisabled={isDownloadLoading}
          >
            {isDownloadLoading ? ' Generating Download' : 'Download Invoice'}
          </MenuItem>
        )}
        {!invoiceReport && !workleaseId && (
          <Tooltip label="No download found for this invoice" placement="left">
            <MenuItem
              icon={<Icon icon="arrow-down-to-bracket" />}
              color={colors.gray[500]}
              isDisabled
            >
              <Text>Download Invoice</Text>
            </MenuItem>
          </Tooltip>
        )}
      </MenuList>
    </Menu>
  );
};
